import React, { useMemo, useCallback, useContext } from "react";
import { Issue } from "@adsk/acc-issues-types";
import {
  CellContext,
  AccessorKeyColumnDef,
  HeaderContext,
} from "@tanstack/react-table";
import { OverflowTooltip } from "@adsk/alloy-react-tooltip";
import styled from "styled-components";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import { getIssueTypeEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueTypes";
import { getIssueRootCausesEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueRootCauses";
import { useTranslations } from "@adsk/acc-issues-translations";
import {
  useCompaniesEntitiesMapByOxygenId,
  useRolesEntitiesMapByOxygenId,
  useUsersEntitiesMapByOxygenId,
} from "@adsk/acc-issues-common/hooks/data/projectMembersHooks";
import {
  useGetCompanyIdToUserNameCountMap,
  getProjectMemberCompanyDisplayName,
} from "@adsk/acc-issues-common/utils/user/oxygenUtils";
import { useFormattedUser } from "@adsk/alloy-provider";
import { useGetProjectActorByOxygenId } from "../../../hooks/useGetProjectActorByOxygenId";
import {
  getProjectMemberDataGetter,
  getMultipleProjectMembersDataGetter,
} from "./dataGetters";
import {
  dateCellRenderer,
  datetimeCellRenderer,
  statusCellRenderer,
  placementCellRenderer,
  typeCellRenderer,
  locationCellRenderer,
  dueDateCellRenderer,
  attachmentsCellRenderer,
} from "./cellRenderers";
import { trackEditSelect } from "../../../utils/analytics/issueActionsTracking";
import {
  editableTitleCellRenderer,
  editableDescriptionCellRenderer,
  editableLocationDetailsCellRenderer,
  editableStatusCellRenderer,
  editableRootCauseCellRenderer,
  editableAssignedToCellRenderer,
  editableTypeCellRenderer,
} from "./InlineEdit/inlineEditableCellRenderer";
import { IssueTableMeta } from "./InlineEdit/inlineEditableCellShared";
import { referencesCellRenderer } from "./referencesCellRenderer";
import Checkbox, { Indeterminate } from "@adsk/alloy-react-checkbox";
import {
  ISSUE_COLUMN_KEYS,
  ISSUE_COLUMN_DATA_KEYS,
  ColumnDataKey,
} from "./columnKeys";
import { CheckboxContainer } from "./issuesTableStyles";
import {
  useFeatureFlag,
  FeatureFlag,
} from "@adsk/acc-issues-core/utils/featureFlags";
import { DeletedIssuesSpaceContext } from "@adsk/acc-issues-core/providers/DeletedIssuesSpaceContext";

export type CellParams = CellContext<Issue, any>;

const OverflowContainer = styled.div`
  overflow: hidden;
`;

const getContainer = (
  params: CellParams
): React.RefObject<HTMLDivElement | null> | undefined => {
  const meta = params.table.options.meta as IssueTableMeta | undefined;
  return meta?.container;
};

const renderIdCell = (params: CellParams) => `#${params.getValue()}`;

const renderOverflowCell = (params: CellParams) => (
  <OverflowTooltip>{params.getValue()}</OverflowTooltip>
);

const renderStatusCell = (params: CellParams) =>
  statusCellRenderer({
    cellData: params.getValue(),
    rowData: params.row?.original,
    column: {},
    container: null,
  });

const renderDatetimeCell = (params: CellParams) =>
  datetimeCellRenderer({
    cellData: params.getValue(),
    rowData: params.row?.original,
    column: {},
    container: null,
  });

const renderLocationCell = (params: CellParams) =>
  locationCellRenderer({
    cellData: params.getValue(),
  });

const renderDueDateCell = (params: CellParams) =>
  dueDateCellRenderer({
    cellData: params.getValue(),
    rowData: params.row?.original,
    column: {},
    container: null,
  });

const renderDateCell = (params: CellParams) =>
  dateCellRenderer({
    cellData: params.getValue(),
    rowData: params.row?.original,
    column: {},
    container: null,
  });

const renderPlacementCell = (params: CellParams) => {
  const {
    row: { original },
  } = params;
  return (
    <OverflowContainer>
      {placementCellRenderer({
        cellData: (original as Issue & { placement?: unknown }).placement,
        rowData: original,
        column: {},
        container: getContainer(params),
      })}
    </OverflowContainer>
  );
};

const renderAttachmentsCell = (params: CellParams) =>
  attachmentsCellRenderer({
    rowData: params.row?.original,
  });

const renderReferencesCell = (params: CellParams) =>
  referencesCellRenderer({
    cellData: params.getValue(),
    rowData: params.row?.original,
    column: {},
    container: null,
  });

export type IssueColumnDef = Omit<
  AccessorKeyColumnDef<Issue, unknown>,
  "accessorKey"
> & {
  accessorKey: ColumnDataKey;
};

export const useAllColumnDefinitions = (): IssueColumnDef[] => {
  const { T } = useTranslations();
  const issueTypeEntitiesMap = useSelector(getIssueTypeEntitiesMap);
  const issueRootCauseEntitiesMap = useSelector(getIssueRootCausesEntitiesMap);
  const { formatDisplayUser } = useFormattedUser();
  const usersEntitiesMap = useUsersEntitiesMapByOxygenId();
  const companiesEntitiesMap = useCompaniesEntitiesMapByOxygenId();
  const rolesEntitiesMap = useRolesEntitiesMapByOxygenId();
  const { value: isInLineEditEnabled } = useFeatureFlag(FeatureFlag.InLineEdit);
  const isDeletedIssuesSpace = useContext(DeletedIssuesSpaceContext);

  const enableInlineEdit = isInLineEditEnabled && !isDeletedIssuesSpace;

  const usersArray = useMemo(
    () => Object.values(usersEntitiesMap),
    [usersEntitiesMap]
  );
  const companyIdToUserNameCountMap =
    useGetCompanyIdToUserNameCountMap(usersArray);

  const getAssigneeName = useGetProjectActorByOxygenId({
    showEmailOnDuplicateName: true,
  });

  const multipleProjectMembersDataGetter = useMemo(
    () =>
      getMultipleProjectMembersDataGetter({
        formatDisplayUser,
        rolesEntitiesMap,
        companiesEntitiesMap,
        companyIdToUserNameCountMap,
        T,
      }),
    [
      formatDisplayUser,
      rolesEntitiesMap,
      companiesEntitiesMap,
      companyIdToUserNameCountMap,
      T,
    ]
  );

  const projectMemberDataGetter = useMemo(
    () =>
      getProjectMemberDataGetter({
        formatDisplayUser,
        rolesEntitiesMap,
        companiesEntitiesMap,
        companyIdToUserNameCountMap,
        T,
      }),
    [
      formatDisplayUser,
      rolesEntitiesMap,
      companiesEntitiesMap,
      companyIdToUserNameCountMap,
      T,
    ]
  );

  const selectHeaderRenderer = useCallback(
    ({
      table: { getRowModel, getSelectedRowModel, toggleAllRowsSelected },
    }: HeaderContext<Issue, unknown>) => {
      const totalRows = getRowModel().rows.filter(
        row => row.getCanSelect() || row.getIsSelected()
      ).length;
      const selectedRows = getSelectedRowModel().rows.length;
      const areAllSelected = totalRows === selectedRows;
      const valueWhenSomeSelected =
        selectedRows > 0 ? Checkbox.INDETERMINATE : false;
      const checkboxState = areAllSelected || valueWhenSomeSelected;

      return (
        <Checkbox
          data-testid="select-all"
          checked={checkboxState}
          onChange={checked => {
            toggleAllRowsSelected(checked === true);
          }}
        />
      );
    },
    []
  );

  const selectCellRenderer = useCallback(
    ({
      row: { getIsSelected, getCanSelect, getToggleSelectedHandler },
    }: CellContext<Issue, unknown>) => {
      const handleChange = (checked: boolean | Indeterminate) => {
        if (checked === true) {
          trackEditSelect();
        }
        const handler = getToggleSelectedHandler();
        handler({ target: { checked } });
      };

      return (
        <CheckboxContainer onClick={e => e.stopPropagation()}>
          <Checkbox
            data-testid="checkbox-cell"
            checked={getIsSelected()}
            disabled={!getCanSelect()}
            onChange={handleChange}
          />
        </CheckboxContainer>
      );
    },
    []
  );

  const typeCategoryCellRenderer = useCallback(
    ({ getValue }: CellParams) => {
      const categoryId = getValue();
      if (!categoryId) return "-";
      const category = issueTypeEntitiesMap[categoryId];
      return <OverflowTooltip>{category?.title}</OverflowTooltip>;
    },
    [issueTypeEntitiesMap]
  );

  const assignedToCellRenderer = useCallback(
    ({ row }: CellParams) => {
      const assignedTo = (row?.original as Issue)?.assignedTo ?? "";
      const assigneeInfo = getAssigneeName(assignedTo);
      return <OverflowTooltip>{assigneeInfo?.name || "-"}</OverflowTooltip>;
    },
    [getAssigneeName]
  );

  const makeProjectMemberCellRenderer = useCallback(
    (columnKey: ISSUE_COLUMN_KEYS) => {
      return ({ row }: CellParams) => (
        <OverflowTooltip>
          {projectMemberDataGetter?.({
            rowData: row?.original,
            column: {
              key: columnKey,
              dataKey: ISSUE_COLUMN_DATA_KEYS[columnKey] as keyof Issue,
            },
          })}
        </OverflowTooltip>
      );
    },
    [projectMemberDataGetter]
  );

  const deletedByCellRenderer = useMemo(
    () => makeProjectMemberCellRenderer(ISSUE_COLUMN_KEYS.DELETED_BY),
    [makeProjectMemberCellRenderer]
  );

  const typeCellRendererWrapper = useCallback(
    (params: CellParams) => {
      return typeCellRenderer({
        cellData: issueTypeEntitiesMap[params.getValue()],
        rowData: params.row?.original,
        column: {},
        container: null,
      });
    },
    [issueTypeEntitiesMap]
  );

  const watchersCellRendererWrapper = useCallback(
    ({ row }: CellParams) => (
      <OverflowTooltip>
        {multipleProjectMembersDataGetter?.({
          rowData: row?.original,
          column: {
            key: ISSUE_COLUMN_KEYS.WATCHERS,
            dataKey: ISSUE_COLUMN_DATA_KEYS[
              ISSUE_COLUMN_KEYS.WATCHERS
            ] as keyof Issue,
          },
        })}
      </OverflowTooltip>
    ),
    [multipleProjectMembersDataGetter]
  );

  const companyCellRendererWrapper = useCallback(
    ({ getValue, row }: CellParams) => {
      const company = getProjectMemberCompanyDisplayName?.({
        usersEntitiesMap: usersEntitiesMap,
        companiesEntitiesMap: companiesEntitiesMap,
        assignedToType: row?.original?.assignedToType,
        id: getValue(),
      });
      return <OverflowTooltip>{company || "-"}</OverflowTooltip>;
    },
    [usersEntitiesMap, companiesEntitiesMap]
  );

  const rootCauseCellRendererWrapper = useCallback(
    ({ getValue }: CellParams) =>
      issueRootCauseEntitiesMap?.[getValue() || ""]?.title || "-",
    [issueRootCauseEntitiesMap]
  );

  const isPublishedCellRendererWrapper = useCallback(
    ({ getValue }: CellParams) =>
      getValue()
        ? T.translate("filterPanel.publishedOptions.published")
        : T.translate("filterPanel.publishedOptions.unpublished"),
    [T]
  );

  const createdByCellRendererWrapper = useMemo(
    () => makeProjectMemberCellRenderer(ISSUE_COLUMN_KEYS.CREATED_BY),
    [makeProjectMemberCellRenderer]
  );

  const updatedByCellRendererWrapper = useMemo(
    () => makeProjectMemberCellRenderer(ISSUE_COLUMN_KEYS.UPDATED_BY),
    [makeProjectMemberCellRenderer]
  );

  return useMemo(() => {
    return [
      {
        id: "select",
        accessorKey: "select",
        size: 30,
        enableResizing: false,
        enableSorting: false,
        header: selectHeaderRenderer,
        cell: selectCellRenderer,
        meta: {
          sticky: "left",
        },
      },
      {
        id: ISSUE_COLUMN_KEYS.ID,
        header: T.translate("issueAttributes.displayId") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.ID],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: false,
        size: 80,
        cell: renderIdCell,
      },
      {
        id: ISSUE_COLUMN_KEYS.TITLE,
        header: T.translate("issueAttributes.title") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.TITLE],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 190,
        minSize: 120,
        cell: enableInlineEdit ? editableTitleCellRenderer : renderOverflowCell,
      },
      {
        id: ISSUE_COLUMN_KEYS.STATUS,
        header: T.translate("issueAttributes.status") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.STATUS],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: false,
        size: 200,
        cell: enableInlineEdit ? editableStatusCellRenderer : renderStatusCell,
      },
      {
        id: ISSUE_COLUMN_KEYS.ISSUE_TYPE_CATEGORY,
        header: T.translate("issueAttributes.issueTypeCategory") as string,
        accessorKey:
          ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.ISSUE_TYPE_CATEGORY],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 150,
        minSize: 70,
        cell: typeCategoryCellRenderer,
      },
      {
        id: ISSUE_COLUMN_KEYS.ISSUE_TYPE,
        header: T.translate("issueAttributes.issueType") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.ISSUE_TYPE],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 180,
        minSize: 100,
        cell: enableInlineEdit
          ? editableTypeCellRenderer
          : typeCellRendererWrapper,
      },
      {
        id: ISSUE_COLUMN_KEYS.DESCRIPTION,
        header: T.translate("issueAttributes.description") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.DESCRIPTION],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 200,
        minSize: 120,
        cell: enableInlineEdit
          ? editableDescriptionCellRenderer
          : renderOverflowCell,
      },
      {
        id: ISSUE_COLUMN_KEYS.ASSIGNED_TO,
        header: T.translate("issueAttributes.assignedTo") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.ASSIGNED_TO],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 150,
        minSize: 100,
        cell: enableInlineEdit
          ? editableAssignedToCellRenderer
          : assignedToCellRenderer,
      },
      {
        id: ISSUE_COLUMN_KEYS.DELETED_BY,
        header: T.translate("issueAttributes.deletedBy") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.DELETED_BY],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 200,
        minSize: 70,
        cell: deletedByCellRenderer,
      },
      {
        id: ISSUE_COLUMN_KEYS.DELETED_ON,
        header: T.translate("issueAttributes.deletedAt") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.DELETED_ON],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 220,
        minSize: 200,
        cell: renderDatetimeCell,
      },
      {
        id: ISSUE_COLUMN_KEYS.WATCHERS,
        header: T.translate("issueAttributes.watchers") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.WATCHERS],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 200,
        minSize: 70,
        cell: watchersCellRendererWrapper,
      },
      {
        id: ISSUE_COLUMN_KEYS.COMPANY,
        header: T.translate("issueAttributes.company") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.COMPANY],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 200,
        minSize: 70,
        cell: companyCellRendererWrapper,
      },
      {
        id: ISSUE_COLUMN_KEYS.LBS_LOCATION,
        header: T.translate("issueAttributes.location") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.LBS_LOCATION],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 120,
        minSize: 100,
        cell: renderLocationCell,
      },
      {
        id: ISSUE_COLUMN_KEYS.LOCATION_DETAILS,
        header: T.translate("issueAttributes.locationDetails") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.LOCATION_DETAILS],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 120,
        minSize: 120,
        cell: enableInlineEdit
          ? editableLocationDetailsCellRenderer
          : renderOverflowCell,
      },
      {
        id: ISSUE_COLUMN_KEYS.DUE_DATE,
        header: T.translate("issueAttributes.dueDate") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.DUE_DATE],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 110,
        minSize: 90,
        cell: renderDueDateCell,
      },
      {
        id: ISSUE_COLUMN_KEYS.START_DATE,
        header: T.translate("issueAttributes.startDate") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.START_DATE],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 220,
        minSize: 120,
        cell: renderDateCell,
      },
      {
        id: ISSUE_COLUMN_KEYS.PLACEMENT,
        header: T.translate("issueAttributes.placement") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.PLACEMENT],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        minSize: 160,
        cell: renderPlacementCell,
      },
      {
        id: ISSUE_COLUMN_KEYS.ROOT_CAUSE,
        header: T.translate("issueAttributes.rootCause") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.ROOT_CAUSE],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 160,
        minSize: 100,
        cell: enableInlineEdit
          ? editableRootCauseCellRenderer
          : rootCauseCellRendererWrapper,
      },
      {
        id: ISSUE_COLUMN_KEYS.IS_PUBLISHED,
        header: T.translate("filterPanel.publishedOptions.published") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.IS_PUBLISHED],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 120,
        minSize: 120,
        cell: isPublishedCellRendererWrapper,
      },
      {
        id: ISSUE_COLUMN_KEYS.CREATED_BY,
        header: T.translate("issueAttributes.createdBy") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.CREATED_BY],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 200,
        minSize: 70,
        cell: createdByCellRendererWrapper,
      },
      {
        id: ISSUE_COLUMN_KEYS.CREATED_AT,
        header: T.translate("issueAttributes.createdAt") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.CREATED_AT],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 220,
        minSize: 200,
        cell: renderDatetimeCell,
      },
      {
        id: ISSUE_COLUMN_KEYS.UPDATED_BY,
        header: T.translate("issueAttributes.updatedBy") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.UPDATED_BY],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 200,
        minSize: 70,
        cell: updatedByCellRendererWrapper,
      },
      {
        id: ISSUE_COLUMN_KEYS.UPDATED_AT,
        header: T.translate("issueAttributes.updatedAt") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.UPDATED_AT],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 220,
        minSize: 200,
        cell: renderDatetimeCell,
      },
      {
        id: ISSUE_COLUMN_KEYS.ATTACHMENTS,
        header: T.translate("issueAttributes.attachments") as string,
        accessorKey: "attachmentCount",
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 160,
        minSize: 120,
        cell: renderAttachmentsCell,
      },
      {
        id: ISSUE_COLUMN_KEYS.REFERENCES,
        header: T.translate("issueAttributes.references") as string,
        accessorKey: ISSUE_COLUMN_DATA_KEYS[ISSUE_COLUMN_KEYS.REFERENCES],
        enableSorting: false,
        sortDescFirst: false,
        enableResizing: true,
        size: 160,
        minSize: 120,
        cell: renderReferencesCell,
      },
    ];
  }, [
    T,
    enableInlineEdit,
    selectHeaderRenderer,
    selectCellRenderer,
    typeCategoryCellRenderer,
    typeCellRendererWrapper,
    assignedToCellRenderer,
    deletedByCellRenderer,
    watchersCellRendererWrapper,
    companyCellRendererWrapper,
    rootCauseCellRendererWrapper,
    isPublishedCellRendererWrapper,
    createdByCellRendererWrapper,
    updatedByCellRendererWrapper,
  ]);
};
